<script setup lang="ts">
import {BubbleMenu, type Editor, FloatingMenu} from "@tiptap/vue-3";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import type { Level } from "@tiptap/extension-heading";
import type { ObjectId } from "~/composables/charon";
import type { FloatingMenuPluginProps } from "@tiptap/extension-floating-menu";

const { t } = useI18n()

const props = defineProps<{
  editor: Editor
  headings: Level[]
}>()

const extensions = computed(() =>
  props.editor
    .extensionManager
    .extensions
    .map(extension => extension.name)
)

const fileViewerOpen = ref(false)

const youtube = ref({
  open: false,
  href: "",
})

function insertFiles(fileIds: ObjectId[]) {
  props.editor.commands.insertContent({
    type: 'figure',
    content: [
      {
        type: 'gallery',
        content: fileIds.map(fileId => ({
          type: 'charon-file',
          attrs: { fileId: fileId }
        }))
      },
      {
        type: 'figcaption',
        content: [
          {
            type: 'text',
            text: 'Figure Caption'
          }
        ]
      },
    ]
  })
}

function submitYoutube() {
  props.editor
    .chain()
    .setYoutubeVideo({
      src: unref(youtube.value.href),
    })
    .focus()
    .run()
  youtube.value.open = false
  youtube.value.href = ""
}

function cancelYoutube() {
  youtube.value.open = false
  youtube.value.href = ""
}

const shouldShow: FloatingMenuPluginProps["shouldShow"] = ({ view, state }) => {
  const { selection } = state
  const { $anchor, empty } = selection
  const isRootDepth = $anchor.depth === 1
  const isHighlightSection = $anchor.node(Math.max(0, $anchor.depth - 1)).type.name === "highlight-section"
  const isEmptyTextBlock = $anchor.parent.isTextblock && !$anchor.parent.type.spec.code && !$anchor.parent.textContent

  return view.hasFocus()
    && empty
    && (isRootDepth || isHighlightSection)
    && isEmptyTextBlock
    && props.editor.isEditable
}
</script>

<template>
  <FloatingMenu
    :editor="props.editor"
    :tippy-options="{ duration: 100, zIndex: 11 }"
    :should-show="shouldShow"
    pluginKey="floating-block"
    class="bg-secondary-highlighted w-min rounded flex flex-row shadow"
  >
    <button
      v-if="extensions.includes('paragraph')"
      :title="t('jc.component.editor.button.paragraph.title')"
      @click="props.editor.chain().focus().setParagraph().run()"
      :aria-selected="props.editor.isActive('paragraph')"
      class="p-1.5 text-white aria-selected:text-primary hover:text-primary-highlighted active:text-primary-selected disabled:text-opacity-50"
    >
      <font-awesome-icon
        icon="text"
        fixed-width
      />
    </button>
    <template v-for="level in props.headings" :key="level">
      <button
        v-if="extensions.includes('heading')"
        :title="t(`jc.component.editor.button.heading.${level}.title`)"
        @click="editor.chain().focus().toggleHeading({ level }).run()"
        :aria-selected="props.editor.isActive('heading', { level })"
        class="p-1.5 text-white aria-selected:text-primary hover:text-primary-highlighted active:text-primary-selected disabled:text-opacity-50"
      >
        <font-awesome-icon
          :icon="`h${level}`"
          fixed-width
        />
      </button>
    </template>
    <button
      v-if="extensions.includes('code')"
      :title="t('jc.component.editor.button.codeBlock.title')"
      @click="props.editor.chain().focus().toggleCodeBlock().run()"
      :aria-selected="props.editor.isActive('codeBlock')"
      class="p-1.5 text-white aria-selected:text-primary hover:text-primary-highlighted active:text-primary-selected disabled:text-opacity-50"
    >
      <font-awesome-icon
        icon="code"
        fixed-width
      />
    </button>
    <button
      v-if="extensions.includes('blockquote')"
      :title="t('jc.component.editor.button.blockQuote.title')"
      @click="props.editor.chain().focus().toggleBlockquote().run()"
      :aria-selected="props.editor.isActive('blockquote')"
      class="p-1.5 text-white aria-selected:text-primary hover:text-primary-highlighted active:text-primary-selected disabled:text-opacity-50"
    >
      <font-awesome-icon
        icon="block-quote"
        fixed-width
      />
    </button>
    <button
      v-if="extensions.includes('bulletList')"
      :title="t('jc.component.editor.button.bulletList.title')"
      @click="props.editor.chain().focus().toggleBulletList().run()"
      :aria-selected="props.editor.isActive('bulletList')"
      class="p-1.5 text-white aria-selected:text-primary hover:text-primary-highlighted active:text-primary-selected disabled:text-opacity-50"
    >
      <font-awesome-icon
        icon="list-ul"
        fixed-width
      />
    </button>
    <button
      v-if="extensions.includes('orderedList')"
      :title="t('jc.component.editor.button.orderedList.title')"
      @click="props.editor.chain().focus().toggleOrderedList().run()"
      :aria-selected="props.editor.isActive('orderedList')"
      class="p-1.5 text-white aria-selected:text-primary hover:text-primary-highlighted active:text-primary-selected disabled:text-opacity-50"
    >
      <font-awesome-icon
        icon="list-ol"
        fixed-width
      />
    </button>
    <button
      v-if="extensions.includes('horizontalRule')"
      :title="t('jc.component.editor.button.horizontalRule.title')"
      @click="props.editor.chain().focus().setHorizontalRule().run()"
      :aria-selected="props.editor.isActive('horizontalRule')"
      class="p-1.5 text-white aria-selected:text-primary hover:text-primary-highlighted active:text-primary-selected disabled:text-opacity-50"
    >
      <font-awesome-icon
        icon="horizontal-rule"
        fixed-width
      />
    </button>
    <button
      v-if="extensions.includes('highlight-section')"
      :title="t('jc.component.editor.button.highlightSection.title')"
      @click="props.editor.chain().focus().toggleHighlightSection().run()"
      :aria-selected="props.editor.isActive('highlight-section')"
      class="p-1.5 text-white aria-selected:text-primary hover:text-primary-highlighted active:text-primary-selected disabled:text-opacity-50"
    >
      <font-awesome-icon
        icon="sparkles"
        fixed-width
      />
    </button>
    <button
      v-if="extensions.includes('youtube')"
      :title="t('jc.component.editor.button.youtube.title')"
      @click="youtube.open = true"
      :aria-selected="props.editor.isActive('youtube')"
      class="p-1.5 text-white aria-selected:text-primary hover:text-primary-highlighted active:text-primary-selected disabled:text-opacity-50"
    >
      <font-awesome-icon
        :icon="{ prefix: 'fab', iconName: 'youtube' }"
        fixed-width
      />
    </button>
    <button
      v-if="extensions.includes('gallery') && extensions.includes('charon-file')"
      :title="t('jc.component.editor.button.gallery.title')"
      @click="fileViewerOpen = true"
      :aria-selected="props.editor.isActive('gallery')"
      class="p-1.5 text-white aria-selected:text-primary hover:text-primary-highlighted active:text-primary-selected disabled:text-opacity-50"
    >
      <font-awesome-icon
        icon="files"
        fixed-width
      />
    </button>
  </FloatingMenu>

  <FileViewer
    v-if="fileViewerOpen"
    select="multi"
    @click:select="insertFiles"
    @click:close="fileViewerOpen = false"
  />

  <Modal
    id="youtube-modal"
    :open="youtube.open"
    @cancel="cancelYoutube"
  >
    <ModalHeader>
      <span class="">
        {{ t('jc.component.editor.button.youtube.input.heading') }}
      </span>
    </ModalHeader>
    <ModalContent>
      <label class="mt-4 w-full flex flex-col justify-center">
        <span class="text-xs">{{ t('jc.component.editor.button.youtube.input.label') }}</span>
        <input
          ref="input"
          :placeholder="t('jc.component.editor.button.youtube.input.placeholder')"
          type="url"
          v-model="youtube.href"
          class="mt-1 block rounded text-gray-900 invalid:[&:not(:focus)]:border-red-500 peer"
        >
        <span class="mt-2 hidden text-xs text-red-500 peer-[&:not(:focus):invalid]:block">
          {{ t('jc.component.editor.button.youtube.input.invalid') }}
        </span>
      </label>
    </ModalContent>
    <ModalActions>
      <button
        type="submit"
        @click="submitYoutube"
        :title="t('jc.component.editor.button.youtube.input.submitButton.title')"
        class="inline-flex gap-2 w-full justify-center rounded bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-highlighted active:bg-primary-selected disabled:opacity-50 mobile:ml-3 mobile:w-auto"
      >
        <font-awesome-icon
          fixed-width
          icon="save"
          size="lg"
        />
        {{ t('jc.component.editor.button.youtube.input.submitButton.label') }}
      </button>
    </ModalActions>
  </Modal>
</template>

<style scoped lang="postcss">

</style>
